import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { soVN } from '../../../lib/dinh-dang';
import { phanTram, TheChuThich, useMucTuongTac } from '../../nhat-ky-truy-vet/chung/components/BieuDo';
import { MAU_RA, MAU_VAO } from '../constants';
import { viTriTheDong, xepHang, type DongXepHang } from '../nghiep-vu/token-ai';

/**
 * Bảng xếp hạng ngang: tên, tổng token Fox, thanh chia đầu vào và đầu ra, dòng số đầy đủ. Tên dài và nhiều mục nên thanh
 * ngang đọc được hơn cột đứng; số in sẵn nên màu không phải nguồn thông tin duy nhất.
 *
 * Rê, chạm hoặc focus vào dòng hiện thẻ chú thích như biểu đồ cột (`useMucTuongTac`, `TheChuThich`): cả dãy là một điểm
 * dừng Tab, mũi tên đi giữa các dòng. Câu đầy đủ cho trình đọc màn hình ở `aria-label` của từng dòng.
 */
export function XepHangNgang({
  dong,
  tran,
  nhanKhongGan,
  nhanDanhSach,
}: {
  dong: readonly DongXepHang[];
  tran: number;
  /** Tên cho dòng `khoa: null`. */
  nhanKhongGan: string;
  nhanDanhSach: string;
}) {
  const { t, locale } = useT();
  const { dau, khac, soKhac } = xepHang(dong, tran);
  const lonNhat = Math.max(1, ...dau.map((d) => d.tong), khac?.tong ?? 0);
  const hang = [
    ...dau.map((d) => ({ ...d, ten: d.ten ?? d.khoa ?? nhanKhongGan, khoaDong: d.khoa ?? '-' })),
    ...(khac ? [{ ...khac, ten: t('manTokenAi.khac', { so: soVN(soKhac, locale) }), khoaDong: '--khac' }] : []),
  ];
  const { dang, vongFocus, roiKhoi, thuocTinh, cacMuc } = useMucTuongTac(hang.length);
  const dangChon = dang === null ? undefined : hang[dang];
  const rongThanh = (tong: number) => Math.max((tong / lonNhat) * 100, tong > 0 ? 1.5 : 0);

  return (
    <div className="relative" onPointerLeave={roiKhoi}>
      <ol aria-label={nhanDanhSach} className="-mx-2 space-y-1">
        {hang.map((d, i) => {
          const phanVao = d.tong > 0 ? (d.vao / d.tong) * 100 : 0;
          const tt = thuocTinh(i);
          return (
            <li key={d.khoaDong} className="min-w-0">
              <div
                {...tt}
                role="img"
                aria-label={t('manTokenAi.moTaDong', {
                  ten: d.ten,
                  vao: soVN(d.vao, locale),
                  ra: soVN(d.ra, locale),
                  tong: soVN(d.tong, locale),
                  luot: soVN(d.luot, locale),
                })}
                className={cn(
                  'rounded px-2 py-1 outline-none transition-colors',
                  dang === i && 'bg-subtle',
                  vongFocus === i && 'ring-2 ring-primary-500',
                )}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 truncate text-sm text-content-primary">{d.ten}</span>
                  <span className="shrink-0 text-sm font-medium tabular-nums text-content-primary">{soVN(d.tong, locale)}</span>
                </div>
                <span aria-hidden className="mt-1 flex h-1.5 gap-0.5 overflow-hidden rounded-full bg-subtle">
                  <span className="flex h-full gap-0.5" style={{ width: `${rongThanh(d.tong)}%` }}>
                    {d.vao > 0 && <span className="h-full rounded-full" style={{ width: `${phanVao}%`, backgroundColor: MAU_VAO }} />}
                    {d.ra > 0 && <span className="h-full flex-1 rounded-full" style={{ backgroundColor: MAU_RA }} />}
                  </span>
                </span>
                <p className="mt-0.5 text-2xs tabular-nums text-content-muted">
                  {t('manTokenAi.dongSo', { vao: soVN(d.vao, locale), ra: soVN(d.ra, locale), luot: soVN(d.luot, locale) })}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
      {dangChon && dang !== null ? (
        <TheChuThich
          key={dang}
          tieuDe={dangChon.ten}
          dong={[
            { nhan: t('manTokenAi.dauVao'), mau: MAU_VAO, giaTri: soVN(dangChon.vao, locale), phu: phanTram(dangChon.vao, dangChon.tong, locale) },
            { nhan: t('manTokenAi.dauRa'), mau: MAU_RA, giaTri: soVN(dangChon.ra, locale), phu: phanTram(dangChon.ra, dangChon.tong, locale) },
            { nhan: t('manTokenAi.tong'), giaTri: soVN(dangChon.tong, locale), phu: '', dam: true },
            { nhan: t('manTokenAi.soLuot'), giaTri: soVN(dangChon.luot, locale), phu: '' },
          ]}
          datCho={(the) => {
            const el = cacMuc.current[dang];
            const khung = el?.parentElement?.parentElement?.parentElement;
            if (!el || !khung) return { left: 0, top: 0 };
            const o = el.getBoundingClientRect();
            const k = khung.getBoundingClientRect();
            return viTriTheDong(
              { tren: o.top - k.top, duoi: o.bottom - k.top, dauThanh: o.left - k.left + (o.width * rongThanh(dangChon.tong)) / 100 },
              the,
              { rongKhung: k.width, caoKhung: k.height },
            );
          }}
        />
      ) : null}
    </div>
  );
}
